import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_READABLE_BACKEND_URL!;

export interface AuthStatus {
  signedIn: boolean;
  status?: "verifying" | "purchasing" | "premium";
  uid?: string;
  email?: string;
  emailVerified?: boolean;
  expire?: number;
}

export interface SignUpResult {
  ok?: boolean;
  uid?: string;
  email?: string;
  emailVerified?: boolean;
  error?: string;
}

export interface LoginResult {
  ok?: boolean;
  uid?: string;
  email?: string;
  emailVerified?: boolean;
  error?: string;
}

export interface VerifyEmailResult {
  ok?: boolean;
  error?: string;
}

export interface VerifyPasswordResetCodeResult {
  ok?: boolean;
  email?: string;
  error?: string;
}

export interface ResetPasswordResult {
  ok?: boolean;
  error?: string;
}

export interface AccessTokenResult {
  ok?: boolean;
  accessToken?: string;
  error?: string;
}

export interface BillingPortalResult {
  ok?: boolean;
  url?: string;
  error?: string;
}

export interface CheckoutSessionResult {
  ok?: boolean;
  url?: string;
  error?: string;
}

export interface SubscriptionCountResult {
  ok?: boolean;
  count?: number;
  error?: string;
}

/**
 * 新規ユーザー登録
 */
export const signUp = async (
  email: string,
  password: string,
): Promise<SignUpResult> => {
  try {
    const response = await axios.post(
      BACKEND_URL + "auth/signup/",
      { email, password },
      {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error("SignUp error:", error);
    return { error: "unknown" };
  }
};

/**
 * ログイン
 */
export const login = async (
  email: string,
  password: string,
): Promise<LoginResult> => {
  try {
    const response = await axios.post(
      BACKEND_URL + "auth/login/",
      { email, password },
      {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Login error:", error);
    return { error: "login-failed" };
  }
};

/**
 * ログアウト
 */
export const logout = async (): Promise<void> => {
  try {
    await axios.post(
      BACKEND_URL + "auth/logout/",
      {},
      {
        withCredentials: true,
      },
    );
  } catch (error) {
    console.error("Logout error:", error);
  }
};

/**
 * 認証状態を取得
 */
export const getAuthStatus = async (): Promise<AuthStatus> => {
  try {
    const response = await axios.get(BACKEND_URL + "auth/status/", {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("GetAuthStatus error:", error);
    return { signedIn: false };
  }
};

/**
 * 認証状態を更新して取得（メール確認後などに使用）
 */
export const refreshAuthStatus = async (): Promise<AuthStatus> => {
  try {
    const response = await axios.post(
      BACKEND_URL + "auth/refresh-status/",
      {},
      {
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error("RefreshAuthStatus error:", error);
    return { signedIn: false };
  }
};

/**
 * メール確認アクションを実行
 */
export const verifyEmailAction = async (
  oobCode: string,
): Promise<VerifyEmailResult> => {
  try {
    const response = await axios.post(
      BACKEND_URL + "auth/verify-email-action/",
      { oobCode },
      {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error("VerifyEmailAction error:", error);
    return { error: "verification-failed" };
  }
};

/**
 * パスワードリセットコードを検証
 */
export const verifyPasswordResetCode = async (
  oobCode: string,
): Promise<VerifyPasswordResetCodeResult> => {
  try {
    const response = await axios.post(
      BACKEND_URL + "auth/verify-password-reset-code/",
      { oobCode },
      {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error("VerifyPasswordResetCode error:", error);
    return { error: "invalid-code" };
  }
};

/**
 * パスワードリセットを実行
 */
export const resetPasswordAction = async (
  oobCode: string,
  newPassword: string,
): Promise<ResetPasswordResult> => {
  try {
    const response = await axios.post(
      BACKEND_URL + "auth/reset-password-action/",
      { oobCode, newPassword },
      {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error("ResetPasswordAction error:", error);
    return { error: "reset-failed" };
  }
};

export interface AutoLoginResult {
  ok?: boolean;
  uid?: string;
  email?: string;
  emailVerified?: boolean;
  error?: string;
}

/**
 * 一時トークンを使用して自動ログイン
 */
export const autoLogin = async (
  autoLoginToken: string,
): Promise<AutoLoginResult> => {
  try {
    const response = await axios.post(
      BACKEND_URL + "auth/auto-login/",
      { autoLoginToken },
      {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error("AutoLogin error:", error);
    return { error: "auto-login-failed" };
  }
};

/**
 * アクセストークンを取得（Chrome拡張機能用）
 */
export const getAccessToken = async (): Promise<AccessTokenResult> => {
  try {
    const response = await axios.get(BACKEND_URL + "auth/access-token/", {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("GetAccessToken error:", error);
    return { error: "token-failed" };
  }
};

/**
 * Stripe請求ポータルのURLを取得
 */
export const getBillingPortalUrl = async (
  returnUrl?: string,
  locale?: string,
): Promise<BillingPortalResult> => {
  try {
    const response = await axios.post(
      BACKEND_URL + "auth/billing-portal/",
      { returnUrl, locale },
      {
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error("GetBillingPortalUrl error:", error);
    return { error: "portal-failed" };
  }
};

/**
 * 確認メールを送信
 */
export const sendVerificationEmail = async (
  email: string,
  continueUrl: string,
): Promise<void> => {
  await axios.post(
    BACKEND_URL + "verify-email/",
    {
      email: email,
      url: continueUrl,
    },
    {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    },
  );
};

/**
 * パスワードリセットメールを送信
 */
export const sendPasswordResetEmail = async (
  email: string,
  continueUrl: string,
): Promise<void> => {
  await axios.post(
    BACKEND_URL + "reset-password/",
    {
      email: email,
      url: continueUrl,
    },
    {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    },
  );
};

/**
 * Stripeチェックアウトセッションを作成
 */
export const createCheckoutSession = async (
  plan: string,
  successUrl: string,
  cancelUrl: string,
): Promise<CheckoutSessionResult> => {
  try {
    const response = await axios.post(
      BACKEND_URL + "auth/create-checkout-session/",
      { plan, successUrl, cancelUrl },
      {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error("CreateCheckoutSession error:", error);
    return { error: "checkout-failed" };
  }
};

/**
 * サブスクリプション数を取得
 */
export const getSubscriptionCount =
  async (): Promise<SubscriptionCountResult> => {
    try {
      const response = await axios.get(
        BACKEND_URL + "auth/subscription-count/",
        {
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("GetSubscriptionCount error:", error);
      return { error: "count-failed" };
    }
  };
